import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { authService } from '../../services/auth';
import { toastService } from '../../services/toast';

const AdminLogin = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // التحقق من الدخول عبر البوابة السرية وحالة تسجيل الدخول
  useEffect(() => {
    const checkAccess = async () => {
      try {
        // التحقق من وجود توكن في sessionStorage
        const token = sessionStorage.getItem('token');
        const userType = sessionStorage.getItem('userType');
        
        // التحقق من الصلاحيات بدلاً من نوع المستخدم
        // النظام الجديد يعتمد على الصلاحيات الفعلية وليس نوع المستخدم
        const userPermissions = JSON.parse(sessionStorage.getItem('userPermissions') || '[]');
        if (token && userPermissions.includes('access_dashboard')) {
          // إذا كان المستخدم لديه الصلاحية المطلوبة، توجيهه للوحة التحكم
          navigate('/admin/dashboard');
          return;
        }
        
        // إلغاء التحقق من المرور عبر البوابة السرية
        // للسماح بالدخول المباشر لصفحة تسجيل الدخول
        
        // منح صلاحية الوصول المباشر عند الحاجة
        if (!sessionStorage.getItem('gateway_access')) {
          sessionStorage.setItem('gateway_access', JSON.stringify({
            granted: true,
            timestamp: new Date().getTime(),
            direct: true
          }));
        }
      } catch (error) {
        console.error('خطأ في التحقق من حالة الوصول:', error);
        // في حالة خطأ، عرض رسالة خطأ نظام
        document.body.innerHTML = '<div style="background-color: black; color: #22aaff; height: 100vh; width: 100vw; display: flex; align-items: center; justify-content: center; font-family: monospace; font-size: 24px; text-align: center; flex-direction: column;">' +
          '<h1 style="color: #22aaff; text-shadow: 0 0 10px #22aaff;">SYSTEM ERROR</h1>' +
          '<p>SECURITY VALIDATION FAILURE</p>' +
          '<p style="margin-top: 20px;">SYSTEM REBOOTING...</p>' +
          '</div>';

        // رجوع للصفحة الرئيسية بعد فترة قصيرة
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      }
    };
    
    checkAccess();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!identifier || !password) {
      toastService.error('الرجاء إدخال البريد الإلكتروني/رقم الجوال وكلمة المرور');
      return;
    }

    setIsLoading(true);
    
    try {
      // تنظيف البيانات قبل الإرسال
      const loginData = {
        identifier: identifier.trim(),
        password: password.trim()
      };

      const response = await authService.adminLogin(loginData.identifier, loginData.password);
      toastService.success('تم تسجيل الدخول بنجاح');
      
      // تسجيل معلومات تشخيصية للمساعدة في تحديد المشكلة
      sessionStorage.setItem('nav_attempt', JSON.stringify({
        from: window.location.pathname,
        to: '/admin/dashboard',
        timestamp: new Date().toISOString()
      }));
      
      // استخدام التوجيه المباشر بدلاً من دالة navigate
      window.location.href = '/admin/dashboard';
    } catch (error) {
      toastService.error(error.message || 'حدث خطأ أثناء تسجيل الدخول');
    } finally {
      setIsLoading(false);
    }
  };

  // التحقق من وجود تأثير الظهور التدريجي في sessionStorage
  React.useEffect(() => {
    const shouldFadeIn = sessionStorage.getItem('adminFadeIn');
    
    if (shouldFadeIn === 'true') {
      // إزالة العلامة بعد استخدامها
      sessionStorage.removeItem('adminFadeIn');
      
      // إضافة كلاس الظهور التدريجي للجسم
      document.body.classList.add('fade-in-admin');
      
      // إضافة CSS للتأثير
      const style = document.createElement('style');
      style.innerHTML = `
        .fade-in-admin {
          animation: adminFadeIn 1s ease-out forwards;
        }
        @keyframes adminFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `;
      document.head.appendChild(style);
      
      // إزالة الكلاس بعد انتهاء الأنيميشن
      setTimeout(() => {
        document.body.classList.remove('fade-in-admin');
      }, 1000);
    }
  }, []);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      {/* Background Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none section-bg">
        <motion.div
          initial={{ scale: 0.9, opacity: 0.5 }}
          animate={{ 
            scale: [0.9, 1.1, 0.9],
            opacity: [0.5, 0.7, 0.5]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 dark:bg-purple-500/20 rounded-full blur-3xl"
        />
        <motion.div
          initial={{ scale: 0.9, opacity: 0.5 }}
          animate={{ 
            scale: [0.9, 1.1, 0.9],
            opacity: [0.5, 0.7, 0.5]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-purple-500/10 dark:bg-blue-500/20 rounded-full blur-3xl"
        />
      </div>

      <div className="max-w-md w-full space-y-8 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl p-8 rounded-[0.5rem] [border-top-right-radius:3rem] [border-bottom-left-radius:3rem] shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] dark:shadow-2xl border border-gray-300 dark:border-gray-700">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            تسجيل دخول الإدارة
          </h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            أهلاً بك مرة أخرى
          </p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="relative">
              <input
                id="identifier"
                name="identifier"
                type="text"
                required
                className="peer h-10 max-sm:h-12 w-full px-3 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent align-middle"
                placeholder="أدخل البريد الإلكتروني أو رقم الجوال"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                disabled={isLoading}
                dir="rtl"
              />
              <label 
                htmlFor="identifier" 
                className="absolute right-3 -top-2.5 bg-gray-50 dark:bg-gray-700 px-1 text-xs font-medium text-gray-700 dark:text-gray-300 transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-500 peer-placeholder-shown:dark:text-gray-400 peer-placeholder-shown:top-3 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-gray-700 peer-focus:dark:text-gray-300 flex items-center"
              >
                البريد الإلكتروني أو رقم الجوال
              </label>
            </div>
            <div className="relative">
              <input
                id="password"
                name="password"
                type="password"
                required
                className="peer h-10 max-sm:h-12 w-full px-3 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent align-middle"
                placeholder="أدخل كلمة المرور"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                dir="rtl"
              />
              <label 
                htmlFor="password" 
                className="absolute right-3 -top-2.5 bg-gray-50 dark:bg-gray-700 px-1 text-xs font-medium text-gray-700 dark:text-gray-300 transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-500 peer-placeholder-shown:dark:text-gray-400 peer-placeholder-shown:top-3 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-gray-700 peer-focus:dark:text-gray-300 flex items-center"
              >
                كلمة المرور
              </label>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-full text-white bg-blue-500 hover:bg-blue-600 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white dark:border-gray-900 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                "تسجيل الدخول"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
